import React from 'react';
import { Download, Printer } from 'lucide-react';
import type { DeclarationData } from '../types';

interface SadFormViewProps {
  sadForm: DeclarationData | null;
  generateXML: () => void;
}

const SadFormView: React.FC<SadFormViewProps> = ({ sadForm, generateXML }) => {
  if (!sadForm) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-slate-700">No SAD form to display.</h2>
        <p className="text-slate-500">Please review data and generate the form first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">SAD Form Preview</h2>
          <p className="text-gray-400">Single Administrative Document (ASYCUDA World Compatible)</p>
        </div>
        <div className="flex gap-3">
            <button className="bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-all">
                <Printer size={20} />
            </button>
            <button
            onClick={generateXML}
            className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
            >
            <Download size={20} />
            Generate XML
            </button>
        </div>
      </div>

      <div className="glass-card rounded-xl p-2 overflow-auto">
        <div className="bg-white text-black rounded-lg min-w-[800px] shadow-2xl">
          {/* Header */}
          <div className="grid grid-cols-12 border-b-2 border-black">
              <div className="col-span-6 p-2 border-r border-black">
                  <h1 className="font-bold text-lg">PAPUA NEW GUINEA CUSTOMS SERVICE</h1>
                  <p className="text-xs">Single Administrative Document</p>
              </div>
              <div className="col-span-2 p-2 border-r border-black">
                  <span className="text-xs font-bold block">1. DECLARATION</span>
                  <span className="text-lg font-mono">IM 4</span>
              </div>
              <div className="col-span-4 p-2">
                  <span className="text-xs font-bold block">A OFFICE OF CLEARANCE</span>
                  <span className="text-lg font-mono">POM - Port Moresby</span>
              </div>
          </div>

          {/* Consignor/Consignee */}
          <div className="grid grid-cols-12 border-b border-black">
             <div className="col-span-6 p-2 border-r border-black h-32 relative">
                <span className="text-xs font-bold block">2. CONSIGNOR/EXPORTER</span>
                <p className="text-sm mt-1">{sadForm.exporter.name}</p>
                <p className="text-xs">{sadForm.exporter.address}</p>
                <div className="absolute bottom-2 right-2 text-xs font-mono">No: {sadForm.exporter.tin}</div>
             </div>
             <div className="col-span-6">
                <div className="p-2 border-b border-black h-16">
                     <span className="text-xs font-bold block">14. DECLARANT/AGENT</span>
                     <p className="text-xs font-mono">BORDERLOGIX CORE SYSTEM</p>
                </div>
                <div className="p-2 h-16 relative">
                     <span className="text-xs font-bold block">8. CONSIGNEE/IMPORTER</span>
                     <p className="text-sm font-semibold">{sadForm.importer.name}</p>
                     <div className="absolute bottom-1 right-2 text-xs font-mono">No: {sadForm.importer.tin}</div>
                </div>
             </div>
          </div>

          {/* Transport/Financial */}
          <div className="grid grid-cols-12 border-b border-black text-xs">
              <div className="col-span-6 border-r border-black">
                  <div className="grid grid-cols-2 border-b border-black">
                      <div className="p-1 border-r border-black"><span className="font-bold block">18 IDENTITY OF MEANS OF TRANSPORT</span> {sadForm.shipping.vesselName}</div>
                      <div className="p-1"><span className="font-bold block">19 CTR</span> 1</div>
                  </div>
                  <div className="p-1 border-b border-black"><span className="font-bold block">21 B/L NO</span> {sadForm.shipping.billOfLading}</div>
              </div>
              <div className="col-span-6">
                  <div className="grid grid-cols-3 border-b border-black">
                      <div className="p-1 border-r border-black"><span className="font-bold block">22 CURRENCY</span> {sadForm.financial.currency}</div>
                      <div className="p-1 border-r border-black"><span className="font-bold block">23 EXCH RATE</span> {sadForm.financial.exchangeRate}</div>
                      <div className="p-1"><span className="font-bold block">20 DELIV TERMS</span> {sadForm.shipping.incoterms}</div>
                  </div>
                  <div className="p-2 font-bold text-right text-sm">
                      TOTAL INVOICE: {sadForm.financial.totalInvoiceAmount.toLocaleString()}
                  </div>
              </div>
          </div>

          {/* Items Header */}
          <div className="bg-slate-200 border-b border-black p-1 text-center font-bold text-xs">
              GOODS DESCRIPTION AND CLASSIFICATION
          </div>

          {/* Items */}
          {sadForm.items.map((item, idx) => (
             <div key={idx} className="border-b-2 border-black relative">
                 <div className="grid grid-cols-12 text-xs h-full min-h-[120px]">
                     {/* Box 31 */}
                     <div className="col-span-7 p-2 border-r border-black">
                         <span className="font-bold block mb-1">31 PACKAGES AND DESCRIPTION OF GOODS</span>
                         <p className="uppercase font-mono mb-2">{item.description}</p>
                         <p className="text-[10px] text-slate-500">Marks & Nos: STANDARD PACKING</p>
                         <div className="mt-2 text-[10px] bg-yellow-50 p-1 border border-yellow-200 rounded">
                            <span className="font-bold">Rationale:</span> {item.classificationRationale?.substring(0, 80)}...
                            {item.primaryLaw && <div className="mt-1 text-slate-600 font-mono">Auth: {item.primaryLaw} {item.legalMethodology?.partIIIClause}</div>}
                         </div>
                     </div>
                     {/* Boxes 32-46 */}
                     <div className="col-span-5 grid grid-cols-2">
                         <div className="p-1 border-b border-r border-black"><span className="font-bold block">32 ITEM NO</span> {item.itemNo}</div>
                         <div className="p-1 border-b border-black bg-slate-100"><span className="font-bold block">33 COMMODITY CODE</span> <span className="text-sm font-mono font-bold">{item.commodityCode}</span></div>
                         
                         <div className="p-1 border-b border-r border-black"><span className="font-bold block">34 CTRY ORIG</span> {item.originCountry}</div>
                         <div className="p-1 border-b border-black"><span className="font-bold block">35 GROSS MASS</span> {item.grossMass}</div>
                         
                         <div className="p-1 border-b border-r border-black"><span className="font-bold block">37 PROCEDURE</span> 4000</div>
                         <div className="p-1 border-b border-black"><span className="font-bold block">38 NET MASS</span> {item.netMass}</div>
                         
                         <div className="col-span-2 p-1 border-b border-black bg-slate-50"><span className="font-bold block">42 ITEM PRICE</span> {item.totalValue.toLocaleString()}</div>
                         
                         <div className="col-span-2 p-1">
                             <span className="font-bold block">47 CALCULATION OF TAXES</span>
                             <div className="flex justify-between text-[10px] mt-1 font-mono">
                                 <span>TYPE: Import Duty</span>
                                 <span>RATE: {item.dutyRate || '0%'}</span>
                             </div>
                         </div>
                     </div>
                 </div>
             </div>
          ))}

          {/* Footer */}
          <div className="p-4 grid grid-cols-2 gap-8 text-xs">
              <div className="border border-black p-2 h-20">
                  <span className="font-bold block">54 PLACE AND DATE</span>
                  <p className="mt-4">Port Moresby, {new Date().toLocaleDateString()}</p>
              </div>
              <div className="border border-black p-2 h-20">
                  <span className="font-bold block">SIGNATURE OF DECLARANT</span>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SadFormView;